import { computed, ref, type ComputedRef, type Ref } from 'vue';
import { squareKey } from '@/api/grid';
import type { Coordinate, Square } from '@/api/types';

/*
 * A tap is the claim, and a tap on one of your own gives it back. docs/UX_FLOWS.md.
 *
 * This lives outside the views because two screens now take picks: the play screen, and the
 * host's own manage screen — a host is a player in their own pool from the moment they create it,
 * and sending them to a second screen to tap a square was a step that existed for the rare host
 * who is only running the thing.
 *
 * What it holds is the *intention*: a claim is a round trip, and on a phone at a braai that is a
 * visible pause. The board colours in under the finger, and everything downstream is written
 * against one list of squares rather than against requests in flight.
 */

export interface SquareTapsInput {
  /** The squares the server last told us about. */
  squares: ComputedRef<Square[]> | Ref<Square[]>;
  /** Who is tapping, and the name to write on a square while the answer is in flight. */
  subjectId: ComputedRef<string | null>;
  displayName: ComputedRef<string>;
  /** How many more this person may take right now. A tap past it is refused here, with a reason. */
  allowance: ComputedRef<number>;
  claim: (square: Coordinate) => Promise<void>;
  release: (square: Coordinate) => Promise<void>;
  /** Re-read the game. Called after every tap settles, successfully or not. */
  reload: () => Promise<void>;
  /** Somebody tapped with no allowance left. The backend would refuse it too. */
  onBlocked: () => void;
}

export interface UseSquareTaps {
  /** The grid as it should look right now — server truth, less releases, plus claims. */
  shownSquares: ComputedRef<Square[]>;
  onToggle: (square: Coordinate) => void;
}

const keysOf = (squares: Coordinate[]): Set<string> =>
  new Set(squares.map((square) => squareKey(square.col, square.row)));

export function useSquareTaps(input: SquareTapsInput): UseSquareTaps {
  const claiming = ref<Coordinate[]>([]);
  const releasing = ref<Coordinate[]>([]);

  const forget = (list: Ref<Coordinate[]>, square: Coordinate): void => {
    const key = squareKey(square.col, square.row);
    list.value = list.value.filter((held) => squareKey(held.col, held.row) !== key);
  };

  /**
   * A square already in the server's list is dropped from the optimistic half rather than added
   * twice — otherwise every claim would flicker between the answer landing and this clearing.
   */
  const shownSquares = computed<Square[]>(() => {
    const gone = keysOf(releasing.value);
    const held = input.squares.value.filter(
      (square) => !gone.has(squareKey(square.col, square.row)),
    );
    const heldKeys = keysOf(held);

    const mine = claiming.value
      .filter((square) => {
        const key = squareKey(square.col, square.row);
        return !gone.has(key) && !heldKeys.has(key);
      })
      // `free: false` — this one is being bought. Squares are only ever handed out free at lock
      // time, which is long past the point where anybody can still tap the board.
      .map((square) => ({
        ...square,
        subjectId: input.subjectId.value ?? '',
        displayName: input.displayName.value,
        free: false,
      }));

    return [...held, ...mine];
  });

  /*
   * Taps are serialised rather than dropped while one is in flight. Somebody picking five squares
   * taps five times in a couple of seconds, and a screen that ignored four of them would be worse
   * than the button that used to sit below the fold.
   */
  let queue: Promise<void> = Promise.resolve();
  const enqueue = (work: () => Promise<void>): void => {
    queue = queue.then(work);
  };

  /**
   * The optimistic coordinate is dropped *after* the re-read, so the cell never blinks empty
   * between the request finishing and the fresh grid arriving. Both callers hand their own
   * failures to their own screen, so nothing is swallowed here.
   */
  const settle = async (
    list: Ref<Coordinate[]>,
    square: Coordinate,
    work: () => Promise<void>,
  ): Promise<void> => {
    await work();
    await input.reload();
    forget(list, square);
  };

  function onToggle(square: Coordinate): void {
    const key = squareKey(square.col, square.row);
    const isMine = shownSquares.value.some(
      (held) =>
        squareKey(held.col, held.row) === key && held.subjectId === (input.subjectId.value ?? ''),
    );

    if (isMine) {
      releasing.value = [...releasing.value, square];
      enqueue(() => settle(releasing, square, () => input.release(square)));
      return;
    }

    // Say why rather than letting the tap do nothing visible — the backend would refuse it anyway.
    if (input.allowance.value <= 0) {
      input.onBlocked();
      return;
    }

    claiming.value = [...claiming.value, square];
    enqueue(() => settle(claiming, square, () => input.claim(square)));
  }

  return { shownSquares, onToggle };
}
